import * as iconsLibrary from 'components/Icon/icons.library';
import { LayoutDashboardLeftMenuItemStyle as S } from './LayoutDashboardLeftMenuItem.style';
import { useClassNames } from 'hooks/useClassNames';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useCleanId } from 'hooks/useCleanId';
import { useLeftMenuOpen } from 'hooks/useLeftMenuOpen';
import { useIsReactPage } from 'hooks/useIsReactPage';

export type LeftMenuItem = {
  name?: string;
  icon: keyof typeof iconsLibrary;
  href?: string;
  to?: string;
  exact?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

type LayoutDashboardLeftMenuItemProps = {
  item: LeftMenuItem;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  variant?: 'settings';
};

export const LayoutDashboardLeftMenuItem: React.FC<LayoutDashboardLeftMenuItemProps> = ({
  item,
  className,
  variant,
  onClick,
}) => {
  const uniqueId = useCleanId();
  const isReactPage = useIsReactPage();
  const { isLeftMenuOpen } = useLeftMenuOpen();
  const targetPath = useMemo(() => {
    return item.href || item.to;
  }, [item]);

  const isActive = useMemo(() => {
    const currentPath = window.location.pathname;
    if (!targetPath) return false;
    return item.exact ? currentPath === targetPath : currentPath.startsWith(targetPath);
  }, [targetPath, item]);

  const containerClassNames = useClassNames(
    {
      'is-active': isActive,
      'is-settings': variant === 'settings',
    },
    `${className} left-menu-item layout-dashboard-left-menu-option-${uniqueId} w-full`
  );

  const button = (
    <>
      <S.Button
        severity="ghost"
        label={item.name}
        iconLeft={item.icon}
        className={containerClassNames}
        onClick={onClick}
        disabled={item.disabled}
        data-testid="button-side-bar-menu"
      />
      <Tooltip
        title={item.tooltip || item.name}
        placement="right"
        target={`.layout-dashboard-left-menu-option-${uniqueId}`}
        hidden={isLeftMenuOpen}
      />
    </>
  );

  return targetPath && !item.disabled ? (
    item.to && isReactPage ? (
      <Link to={targetPath}>{button}</Link>
    ) : (
      <a href={targetPath}>{button}</a>
    )
  ) : (
    button
  );
};
